import { AppComponent } from '../components/app.component'
import { Injectable, EventEmitter } from '@angular/core';
import { LanguageService } from '../services/language.service';
import { LoginService } from './login.service';
import { Exam } from 'app/lib/Exam';
import { Task, TaskType, MultipleChoiceOption, RestoreCanvasEvent } from 'app/lib/Task';
import { ServerChannel } from 'app/lib/LoginUtilities';
import { CanvasService } from './canvas.service';
import { CommandsService } from './commands.service';
import { existsSync } from 'fs';


// #########################################################################
//
// This service is used to manage the login-state
//
// #########################################################################

declare var swal: any;



@Injectable()
export class ExamService {
    //error message if exam already finished (or rather its identifier)
    public static EXAM_FINISHED: number = 13;

    //the value of the recognition the pupil enters in the corresponding input field
    private recognitionValue: string = "";

    //the current exam
    private currentExam: Exam = null;

    // the id of the current exam
    private idOfCurrentExam: string;

    // whether the exam Mode is currently on
    private examModeOn_: boolean = false;

    // whether xlogo currently tries to access an exam
    public pollingForExam: boolean = false;

    // whether an exam is currently shutting down (needed for message after exam)
    public shuttingDownExam: boolean = false;

    // emits an event whenever the examMode is either switched on or off
    public examModeChangedEmitter: EventEmitter<void> = new EventEmitter<void>();

    //emits an event, when the pupil's code concerning a particular task has to be reexecuted
    public reExecutionNeededEmitter: EventEmitter<RestoreCanvasEvent> = new EventEmitter<RestoreCanvasEvent>();

    //whether a task change is currently happening
    public taskChangeHappening: boolean = false;

    //the timestamp where the last backup was saved to the server
    private lastBackup: number = 0;

    //the "time distance" between consecutive backups
    private backupTimeThresholdInMillis: number = 5000;

    //the task which the pupil is currently working on
    private selectedTask: Task = null;

    //the timestamp where xlogoonline started to check whether an exam is ready for the pupil on the server
    private startedCheckingForExam: number = 0;

    //the duration how long xlogoonline should check whether an exam is ready for the pupil on the server
    private checkingDurationInMillis: number = 300000; //try to load an exam for five minutes

    //whether xlogoonline currently checks if an exam is available.
    public checkingForExam: boolean = false;

    constructor(private languageService: LanguageService,
        private loginService: LoginService,
        private canvasService: CanvasService,
        private commandsService: CommandsService) {
        this.commandsService.ctEmitter.subscribe(
            () => {
                if (this.examModeIsOn) {
                    this.saveBackupToServer(true);
                }
            }
        );
    }

    // returns the current exam
    public getExam(): Exam {
        return this.currentExam;
    }

    // set the selected task on the exam service (see saveBackup to realize why not only necessary in editorComponent)
    public setSelectedTask(task: Task) {
        this.selectedTask = task;
    }

    // perform the context switch associated to changing the displayed task:
    // 1. save old canvasState to old task and load canvas state of new task
    // 2. same for history. Note that history is manipulated via commandsService
    public switchCanvasAndHistoryState(oldTask: Task, newTask: Task) {
        this.taskChangeHappening = true;
        if (oldTask != null) {
            oldTask.setCanvasState(this.canvasService.getState());
            oldTask.setHistory(this.commandsService.getData());
            this.canvasService.cs();
            this.commandsService.deleteAll();
            this.commandsService.removeAllCommandFromHistory();

        }
        if (newTask != null) {
            var newState = newTask.getCanvasState();
            var hist = newTask.getHistory();
            var commands: string[] = [];
            for (var i = 0; i < hist.length; i++) {
                this.commandsService.addCommand((<any>hist[i]).text);
                commands.push((<any>hist[i]).text);
            }
            this.commandsService.setCommandLineText(newTask.getInputLine());
            if (newState != null) {
                this.canvasService.restoreState(newState);
            } else {
                // if necessary reexecute the history (if arrive here it IS necessary)
                if (commands.length > 0) {
                    var event = new RestoreCanvasEvent();
                    event.commands = commands;
                    event.editorContent = newTask.getEditorContent();
                    this.reExecutionNeededEmitter.emit(event);
                } else {
                    this.canvasService.flushCanvas();
                }
            }
        }
        setTimeout(
            // leave some time for reexicution event to be processed and the following process to be initiated.
            () => this.taskChangeHappening = false, 50
        );

    }

    // turn off the exam mode
    public leaveExamMode(): void {
        if (this.examModeIsOn) {
            this.currentExam = null;
            this.examModeOn_ = false;
            this.examModeChangedEmitter.emit();
            this.shuttingDownExam = true;
            this.canvasService.cs();
            this.commandsService.deleteAll();
            this.commandsService.removeAllCommandFromHistory();
            setTimeout(
                // While shuttingDownExam == true, a notification is displayed
                () => this.shuttingDownExam = false,
                5000
            );
        }
    }

    // save pupils solution to the server
    public saveSolutionToServer(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            var user = this.loginService.getUser();
            if (user == null || this.currentExam == null) {
                console.log("NO USER OR NO EXAM");
                reject();
            } else {
                var xmlhttp = new XMLHttpRequest();
                xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "saveExamSolution/" + this.idOfCurrentExam);
                xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
                xmlhttp.setRequestHeader("Content-Type", "application/json");
                xmlhttp.send(this.currentExam.getForSolutionSave());
                xmlhttp.onload = function () {
                    if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                        resolve();
                    } else {
                        var errorCode = +xmlhttp.getResponseHeader("Response-Code");
                        if (errorCode == ExamService.EXAM_FINISHED) {
                            this.leaveExamMode()
                        }
                        reject();
                    }
                }.bind(this);
            }
        });
    }

    // save the backup to the server
    public saveBackupToServer(force = false): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            var now = Date.now();
            if (!force && now - this.lastBackup <= this.backupTimeThresholdInMillis) {
                // saveBackupToServer is triggered on every change every happening (that's at least the idea). 
                // Don't always want to write changes to the server, otherwise a bit much spam. Thus, save if enough time passed.
                resolve(false);
            } else {
                this.lastBackup = now;
                var user = this.loginService.getUser();
                if (user == null || this.currentExam == null) {
                    console.log("NO USER OR NO EXAM");
                    reject(false);
                } else {
                    if (this.selectedTask != null) {
                        // This needs to be done, because the history is only added to a task upon task switch.
                        // TODO: find a more elegant solution, this is a quick fix
                        this.switchCanvasAndHistoryState(this.selectedTask, this.selectedTask);
                    }
                    var xmlhttp = new XMLHttpRequest();
                    xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "saveExamState/" + this.idOfCurrentExam);
                    xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
                    xmlhttp.setRequestHeader("Content-Type", "application/json");
                    xmlhttp.send(this.currentExam.getForBackupState());
                    xmlhttp.onload = function () {
                        if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                            resolve(true);
                        } else {
                            var errorCode = +xmlhttp.getResponseHeader("Response-Code");
                            if (errorCode == ExamService.EXAM_FINISHED) {
                                this.leaveExamMode()
                            }
                            reject(true);
                        }
                    }.bind(this);
                }
            }
        });
    }

    // check if an exam is available on the server for the pupil.This is performed repeatedly and if success: tryLoadExam() (polling)
    public checkExamsAvailable(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            var user = this.loginService.getUser();
            if (user == null) {
                console.log("NO USER");
                reject();
            } else {
                var xmlhttp = new XMLHttpRequest();
                xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "checkExamAvailablePupil");
                xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
                xmlhttp.send();
                xmlhttp.onload = function () {
                    if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                        var response = JSON.parse(xmlhttp.response);
                        this.idOfCurrentExam = response.exams[0];
                        resolve();
                    } else {
                        //If no exam found, then timeout and try again (except maximal threshold reached)
                        if (Date.now() - this.startedCheckingForExam < this.checkingDurationInMillis) {
                            setTimeout(
                                () => {
                                    this.checkExamsAvailable().then(
                                        () => resolve(),
                                        () => reject()
                                    )
                                },
                                5000
                            );

                        } else {
                            reject()
                        }
                    }
                }.bind(this);
            }
        });
    }

    // try to load the exam from the server (note that this method expects that there is an exam ready for the pupil)
    public tryLoadExam(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            var user = this.loginService.getUser();
            if (user == null) {
                console.log("NO USER");
                reject(false);
            } else if (this.idOfCurrentExam == null) {
                console.log("Cannot load exam without id");
                reject(false);
            } else {
                var xmlhttp = new XMLHttpRequest();
                xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "loadExamForPupil/" + this.idOfCurrentExam);
                xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
                xmlhttp.setRequestHeader("Content-Type", "application/json");
                xmlhttp.send(JSON.stringify({ hw: false }));
                xmlhttp.onload = function () {
                    if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                        // parse response consisting of an exam and potentially a safety-object (that's the backup)
                        var response = JSON.parse(xmlhttp.response);
                        var examObj = JSON.parse(response.exam);
                        var tasksObj = JSON.parse(response.tasks);
                        var safetyObj = JSON.parse(response.safety);
                        this.currentExam = new Exam(this.idOfCurrentExam, examObj.title);
                        var tasks = examObj.tasks;
                        for (var i = 0; i < tasks.length; i++) {
                            var realTask = null;
                            var task = tasks[i];
                            for (var j = 0; j < tasksObj.length; j++) {
                                if (tasksObj[j].id == task.id) {
                                    realTask = tasksObj[j];
                                    break
                                }
                            }
                            var finalTask = new Task(task.id, realTask.description, "", +realTask.tasktype, realTask.skeleton, task.label);
                            if (safetyObj != null && safetyObj.tasks != null) {
                                // initialize current task from backup
                                for (var k = 0; k < safetyObj.tasks.length; k++) {
                                    var realSafetyObj = safetyObj.tasks[k];
                                    if (realSafetyObj.id == task.id) {
                                        finalTask.setEditorContent(realSafetyObj.editorContent);
                                        finalTask.setHistory(realSafetyObj.history);
                                        finalTask.setInputLine(realSafetyObj.inputLine);
                                        finalTask.setChosenButNotHandedInOption(realSafetyObj.chosenButNotHandedInOption);
                                        finalTask.handedIn = realSafetyObj.handedIn;
                                        finalTask.modified = realSafetyObj.modified;
                                        break;
                                    }
                                }
                            }

                            this.currentExam.addTask(finalTask);
                            if (realTask.hasImage) {
                                this.getTaskImageFromServer(this.idOfCurrentExam, finalTask).then(
                                    () => {

                                    },
                                    () => {

                                    }
                                );
                            }
                        }
                        resolve(true);
                    } else {
                        //TODO: implement
                        if (xmlhttp.status == 406) {
                            //exam not started yet
                            reject(true);
                        } else {
                            reject(false);
                        }
                    }
                }.bind(this);
            }
        });
    }

    // task images have to be fetched from the server individually
    private getTaskImageFromServer(examId: string, task: Task): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            var user = this.loginService.getUser();
            var xmlhttp = new XMLHttpRequest();
            xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "loadTaskImageForPupil/" + examId + "/" + task.getId());
            xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
            xmlhttp.setRequestHeader("Content-Type", "application/json");
            xmlhttp.responseType = "blob";
            xmlhttp.send(JSON.stringify({ hw: false }));
            xmlhttp.onload = function () {
                if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                    //TODO: Here is the point where Microsoft Edge crashed, because there is no support for new File(..)
                    //setImageFromBlob and setImage do exactly the same thing, and both work in npm. Have no Microsoft Edge
                    //and thus cannot test it at the moment, but should thus work in Microsoft Edge (File constructor NOT USED
                    //for in if clause)
                    if (/Edge/.test(navigator.userAgent)) {
                        task.setImageFromBlob(xmlhttp.response);
                    } else {
                        task.setImage(new File([xmlhttp.response], task.getId()))
                    }
                    resolve()
                } else {
                    reject()
                }
            }.bind(this);
        });
    }

    // turn on the exam mode, repeatedly try for a time threshold if not successfull on first try.
    public turnOnExamMode() {
        var infoText = this.languageService.translateKey("infoRecognition");
        var htmlContent: string = "<div>" + infoText + "</div> <br> <input type='text' id='recog' class='swal2-input' value='" + this.recognitionValue + "'>";
        swal({
            title: this.languageService.translateKey("announceTitle"),
            html: htmlContent,
            showCancelButton: true,
            cancelButtonText: this.languageService.translateKey("cancelExamModeButton"),
            confirmButtonText: this.languageService.translateKey("enterExamModeButton")
        }).then(
            () => {
                // enter checking mode: repeatedly look if an exam is ready on the server
                this.requestCourseAccess().then(function(value) {
                    //only continue if value was success
                    if(value != "success"){
                        swal({ type: 'error', title: this.languageService.getLang()['unknownAccessCode'], text: this.languageService.getLang()['unknownAccessCodeText'] });
                        return;
                    }

                    console.log("successfully found exam identifier");
                    this.startedCheckingForExam = Date.now();
                    this.checkingForExam = true;

                    //show purple background
                    this.pollingForExam = true;

                    //from here on we know the exam exists, but the teacher might not activate it
                    this.checkExamsAvailable().then(
                        () => {
                            // enter exam mode and leave checking mode
                            this.checkingForExam = false;
                            this.examModeOn_ = true;
                            this.pollExamToLoad();
                        },
                        () => {
                            // leave checking mode (if time exceeded)
                            console.log("Failed to figure out which exam to load");
                            this.checkingForExam = false;
                            this.startedCheckingForExam = 0;
                        }
                    );
                  }.bind(this));
            },
            () => {
                // leave checking mode manually (or refuse to enter it)
                console.log("user aborted exam mode opening");
                this.checkingForExam = false;
                this.startedCheckingForExam = 0;
            }
        );
    }

    // request access to a particular course on the server by providing the corresponding recognition
    private requestCourseAccess() {
        return new Promise(async function (resolve, reject) {
            var element: HTMLInputElement = <HTMLInputElement>document.getElementById("recog");
            var elementvalue: string = element.value;
            this.recognitionValue = elementvalue;
            var user = LoginService.self.getUser();

            if (elementvalue != null && elementvalue != "") {
                var xmlhttp = new XMLHttpRequest();
                xmlhttp.open("POST", ServerChannel.SERVER_ADDRESS + "requestCourseAccess/" + elementvalue);
                xmlhttp.setRequestHeader("Authorization", "bearer " + user.getToken());
                xmlhttp.send();
                xmlhttp.onload = await function () {
                    if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
                        console.log("recognition success");
                        resolve("success");
                    } else {
                        console.log("recognition fail");
                        resolve("failure");
                    }
                }.bind(this);
            }
        }.bind(this));
    }

    // polls exam (repeatedly call tryLoadExam())
    private pollExamToLoad(): void {
        this.pollingForExam = true;
        this.tryLoadExam().then(
            (cond) => {
                this.examModeChangedEmitter.emit();
                this.pollingForExam = false;
            },
            (cond) => {
                if (cond) {
                    setTimeout(
                        () => this.pollExamToLoad(),
                        3000
                    );
                } else {
                    this.pollingForExam = false;
                    this.examModeOn_ = false;
                }
            }
        );
    }

    // public info on whether exam mode is on.
    public examModeIsOn(): boolean {
        //TODO: implement
        return this.examModeOn_;
    }

    public examModeIsOff_binary(): number {
        return this.examModeIsOn() ? 0 : 1;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/exam.service.ts